import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

interface FaqCategoryItem {
  id: number;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
}

export const FaqCategoryIndex: React.FC = () => {
  const { t, i18n } = useTranslation(["faqCategory", "common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [categories, setCategories] = useState<FaqCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const getLocalizedText = (value?: string | null) => {
    if (!value) return "";

    try {
      if (value.startsWith("{")) {
        const parsed = JSON.parse(value);
        return isRTL ? parsed.ar || parsed.en || value : parsed.en || parsed.ar || value;
      }
    } catch {
      return value;
    }

    return value;
  };

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchCategories = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/faqcategory?${query}`, "GET");

      if (!response.success) {
        showErrorToast(response.error || t("faqCategory:messages.fetchError"));
        return;
      }

      setCategories(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t("faqCategory:messages.fetchError"));
      console.error("Error fetching FAQ categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deleteCategory = async (id: number) => {
    if (!window.confirm(t("faqCategory:messages.deleteConfirm"))) {
      return;
    }

    try {
      const response = await adminApi.call(`/faqcategory/${id}`, "DELETE");

      if (!response.success) {
        showErrorToast(response.error || t("faqCategory:messages.deleteError"));
        return;
      }

      showSuccessToast(t("faqCategory:messages.deleteSuccess"));
      fetchCategories(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t("faqCategory:messages.deleteError"));
      console.error("Error deleting FAQ category:", error);
    }
  };

  const columns: Column[] = [
    {
      key: "displayOrder",
      title: t("faqCategory:table.displayOrder"),
      sortable: true,
      width: "110px",
      align: "center"
    },
    {
      key: "name",
      title: t("faqCategory:table.name"),
      sortable: false,
      filterable: true,
      filterKey: "name",
      render: (value) => getLocalizedText(value)
    },
    {
      key: "description",
      title: t("faqCategory:table.description"),
      sortable: false,
      render: (value) => {
        const text = getLocalizedText(value);
        return text ? (text.length > 120 ? `${text.substring(0, 120)}...` : text) : "-";
      }
    },
    {
      key: "isActive",
      title: t("faqCategory:table.status"),
      sortable: true,
      width: "120px",
      align: "center",
      render: (value) => (
        <span className={`badge ${value ? "bg-success" : "bg-danger"}`}>
          {value ? t("faqCategory:status.active") : t("faqCategory:status.inactive")}
        </span>
      )
    },
    {
      key: "actions",
      title: t("faqCategory:table.actions"),
      sortable: false,
      width: "150px",
      align: "center",
      render: (_, item) => (
        <div className="action-buttons">
          {hasPermission("Update FAQ Category") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => navigate(`/faq-categories/edit/${item.id}`)}
              tooltip={t("faqCategory:editFaqCategory")}
            />
          )}
          {hasPermission("Delete FAQ Category") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteCategory(item.id)}
              tooltip={t("faqCategory:deleteFaqCategory")}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <PageLayout
      title={t("faqCategory:title")}
      subtitle={t("faqCategory:subtitle")}
      breadcrumbs={[{ label: t("faqCategory:breadcrumbs.faqCategories") }]}
      className={isRTL ? "rtl" : "ltr"}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={categories}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create FAQ Category") ? {
          label: t("faqCategory:addNew"),
          onClick: () => navigate("/faq-categories/create")
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters((prev) => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchCategories(currentPage, pageSize)}
        emptyMessage={t("faqCategory:messages.noFaqCategories")}
      />
    </PageLayout>
  );
};
